import { EmailOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Link, Stack } from "@mui/material";
import { TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch, useTitle, useTranslation } from "hooks";
import { PublicLayout } from "layouts";
import AuthPageLayout from "pages/auth/AuthPageLayout";
import { authFooterLinkSx, authStackSpacing, fieldSx, iconSx, primaryBtnSx } from "pages/auth/formShared";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useState } from "react";
import { Link as LinkRouter, useNavigate } from "react-router-dom";

const defaultValue = { email: "" };
const defaultValues = defaultValue;

interface Data {
  errors?: typeof defaultValues;
}

export default function ForgotPassword() {
  const [state, setState] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultValue);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch<Data>("POST");

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.forget, {
        body: JSON.stringify({ email: state.email })
      }).catch(console.error);
    },
    [state, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response?.message || "", { variant: "success" });
      const searchParams = new URLSearchParams();
      searchParams.append("email", state.email);
      navigate({ pathname: "/forgot-password/otp", search: searchParams.toString() });
    }
  }, [response, enqueueSnackbar, navigate, state]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error?.message || JSON.stringify(error), { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  useTitle("Forgot Password");

  return (
    <PublicLayout>
      <AuthPageLayout
        title={t["Forgot Password"]}
        subtitle={t["Enter Email to receive OTP"]}
        footer={
          <Link component={LinkRouter} to="/login" sx={authFooterLinkSx}>
            ← {t["Back to sign in"]}
          </Link>
        }
      >
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={authStackSpacing}>
            <TextInput
              autoFocus
              required
              fullWidth
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              label={t["Email Address"]}
              placeholder={t["e.g johndoes@gmail.com"]}
              helperText={errors?.email}
              error={!!errors?.email}
              value={state.email}
              startAdornment={<EmailOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prevState) => ({ ...prevState, email: e.target.value }));
                setErrors((prevState) => ({ ...prevState, email: "" }));
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              fullWidth
              sx={primaryBtnSx}
            >
              {t["Submit"]}
            </Button>
          </Stack>
        </Box>
      </AuthPageLayout>
    </PublicLayout>
  );
}
